<template>
    <span class="geofleet-wrapper print-directives with-sidebar" :class="{
        'menu-collapsed': main_menu_collapsed,
        'menu-extended': !main_menu_collapsed,
        'fullscreen-map': fullScreenMap
    }">
        <!-- Custom sidebar -->
        <golia-sidebar :collapsed="main_menu_collapsed" @toggleMenu="(collapsed) => (main_menu_collapsed = collapsed)"
            :companies="companies" @plan="focus_truck ? truckPlanner(focus_truck) : truckPlanner()"
            @toggleUtilityPanel="toggleUtilityPanel" :menu="menu_items" />
        <golia-loader v-show="loading || rootLoading" :hideable="false"
            :message="loadingTimeout ? __('loading_timeout') : __('loading')"></golia-loader>

        <geofleet-map :class="{
            'geofleet-map--focus': focus_truck,
            'menu-collapsed': main_menu_collapsed,
            'menu-extended': !main_menu_collapsed,
            blurred: blurredMap
        }" map="silica" :advanced="true" :cluster="activeVehicleClustering" :ptvToken="this.$env.ptv_password"
            :trucks="listActiveTrucks" :poi="true" :noAutoCenterMap="mainMapStopAutocenter" @focus="truckFocus"
            @poisLoaded="loadPoisList" @plan="focus_truck ? truckPlanner(focus_truck) : truckPlanner()"
            @mapContextAction="handleMapContextAction" @toggleMenu="(collapsed) => (main_menu_collapsed = collapsed)"
            @fullScreenToggle="(toggle) => (fullScreenMap = toggle)" :isMainMap="true" ref="map">
            <!-- Resources markers quando si cercano risorse -->
            <custom-marker v-for="(resource, index) in resources" :key="'searched-resource-' + index" :item="resource"
                :coordinates="[resource.coordinate.y, resource.coordinate.x]"
                :markerTitle="resource.marker_label | truncate(10, '...')" :iconUnicode="resource.icon_unicode"
                :customizer="{ tiny: true, color_class: resource.type }" :visible="resource.type != 'borsa'"
                @click="focusResource(resource)" />

            <!-- Polygons dell'isocrona -->
            <template v-if="reachableAreasPolygons && reachableAreasPolygons.length > 0">
                <l-polygon v-for="(polygon, index) in reachableAreasPolygons" :key="index" :lat-lngs="polygon">
                </l-polygon>
            </template>

            <!-- Extra Objects from the store -->
            <template v-if="
                mainMapExtraObjects.circles.length > 0 || mainMapExtraObjects.markers.length > 0
            ">
                <custom-marker v-for="(marker, index) in mainMapExtraObjects.markers" :key="'extra-markers' + index"
                    :item="marker" :coordinates="marker.latLng" :markerTitle="marker.title"
                    :iconUnicode="marker.icon_unicode || '#xf041'"
                    :customizer="{ ...(marker.customizer || {}), tiny: true }" @click="handleClick"
                    :visible="true"></custom-marker>

                <l-circle v-for="(circle, index) in mainMapExtraObjects.circles" :key="'extra-circles' + index"
                    :lat-lng="circle.latLng" :radius="circle.radius">
                </l-circle>
            </template>
        </geofleet-map>

        <!-- APP PANELS -->
        <app-panels :utility_panel="utility_panel" :activity_panel="activity_panel"
            @focus="truckFocus" @activity="truckActivity"
            @plan="truckPlanner" @toggleUtilityPanel="toggleUtilityPanel" @lostfocus="lostUtilsPanel"
            @afterEnter="collapsed_details_panel = true" @afterLeave="collapsed_details_panel = false">
            <!-- VEHICLE / DRIVER PANEL (route-driven) -->
            <transition name="slide-fade">
                <router-view />
            </transition>
            <!-- MAIN PANEL -->
            <!-- REFACTOR-006 (Sprint 7): rimosso prop drilling :drivers/:companies/:trucks.
                 MainPanel legge da store (effectiveTrucks/Drivers/Companies → storeTrucks/Drivers/Companies).
                 L'interfaccia prop resta in MainPanel come override per viewSharedLink.vue. -->
            <main-panel class="tiny-panel main-panel" :collapsed="mainCollapsed" :last_update="last_update"
                :activityPanelOpen="activityPanelOpen" @focus="truckFocus" @activity="truckActivity"
                @plan="truckPlanner" @searching="boundVisibleTruck" ref="main_panel" />
        </app-panels>
        <FreightExchangeMain style="display: none;" />
        <config-saving-indicator />
    </span>
</template>

<script>
    import FreightExchangeMain from '@/components/FreightExchangeMain.vue'
    import MainPanel from './components/MainPanel.vue'
    import AppPanels from './components/AppPanels.vue'
    import { LPolygon, LCircle } from 'vue2-leaflet'

    import { useDocumentVisibility } from '@vueuse/core'

    import moment from 'moment'
    import cloneDeep from 'lodash/cloneDeep'

    import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
    import { mapConfig } from '@/composables/useConfig'
    import { deepFreeze } from '@/functions/deepFreeze'
    import { FeatureGroup } from 'leaflet'

    export default {
        name: 'GeoFleet',
        components: {
            FreightExchangeMain,
            LPolygon,
            LCircle,
            MainPanel,
            AppPanels,
            // Config Bus (Sprint 19): badge "salvataggio…" + toast d'errore post-rollback.
            ConfigSavingIndicator: () => import('@/components/commons/ConfigSavingIndicator.vue')
        },
        props: [],
        setup() {
            const documentVisibility = useDocumentVisibility()

            return {
                documentVisibility
            }
        },
        data() {
            return {
                loading: false,
                loadingTimeout: false,
                fullScreenMap: false,
                // REFACTOR-009: rimosso `trucks_ref: {}`. Single source of truth
                // = state.geoFleet.trucks via getter trucksMap (O(1) lookup) e
                // storeTrucks (iterazione per plate). Niente più triplo storage.
                drivers: [],
                profiles: [],
                proveniences: [],
                companies: [],
                pois: [],

                activity_panel: null,
                utility_panel: null,
                // REFACTOR-007 (Sprint 7): pilota la classe 'with-activity-open' di MainPanel
                // via prop reattivo invece di $refs.main_panel.$el.classList.
                activityPanelOpen: false,

                last_update: null,
                collapsed_menu: false,
                collapsed_details_panel: false,
                truck_hooked_list: [],

                main_menu_collapsed: true
            }
        },
        watch: {
            // Full-screen routed pages (settings, POI management, plafond, route
            // analyze/plan, location) hide the vehicle list; the root map and the
            // vehicle details panel keep it open. Centralises what used to be driven
            // by the router-view transition hooks (which collapsed on any page).
            $route: {
                immediate: true,
                handler(route) {
                    if (route.name === 'geofleet.poi_management') {
                        this.closePanelsBeforePoiManagement()
                    }

                    const shouldListStayOpen = ['geofleet.index', 'geofleet.vehicle_details'].includes(
                        route.name
                    )

                    // Skip when already in the desired state: no point committing
                    // a panel state it is already in on initial load / re-navigation.
                    if (shouldListStayOpen === !this.mainCollapsed) return

                    const mutation = shouldListStayOpen ? 'expandPanel' : 'collapsePanel'
                    this[mutation]({ panel: 'main' })
                }
            },
            documentVisibility(newVal, oldVal) {
                if (
                    newVal == 'visible' &&
                    oldVal == 'hidden' &&
                    moment().diff(this.last_update, 'seconds') > 20
                ) {
                    this.$store.commit('setTimedLoading', 500)
                    this.updateData()
                }
            },
            resources(val) {
                if (val.length > 0) {
                    let bounds =
                        val
                            .filter((r) => r.type != 'borsa')
                            .map((s) => {
                                return [s.coordinate.y, s.coordinate.x]
                            }) || []

                    if (bounds.length > 0 && this.mainMapInstance) {
                        // REFACTOR-007 (Sprint 7): mainMapInstance (store) === $refs.map.$refs.map
                        this.fitBoundsOfMap({ bounds: bounds, map: this.mainMapInstance })
                    }
                }
            },
            reachableAreasPolygons(val) {
                if (val.length > 0) {
                    var bounds = []

                    let clonedVal = cloneDeep(val)

                    clonedVal.map((p) => p.map((c) => bounds.push(c)))

                    bounds.map((b) => {
                        b.map((c) => {
                            c[1] = c[1] + 0.05
                        })
                    })

                    if (bounds.length > 0 && this.mainMapInstance) {
                        this.fitBoundsOfMap({
                            bounds: bounds,
                            // REFACTOR-007 (Sprint 7): mainMapInstance (store) === $refs.map.$refs.map
                            map: this.mainMapInstance,
                            padding: [150, 150]
                        })
                    }
                }
            },
            searchResourcesOpen(val) {
                if (val) {
                    this.closePoiManagementRoute()
                    this.activity_panel = null
                }
            },
            focus_truck(val) {
                if (!val) {
                    if (this.plafondOpen) this.$store.commit('closePlafondPanel', 'plafond')
                }
            },
            trucksEvents(val) {
                if (val.length > 0) {
                    if (this.getTrucksEventsSinceLastEventId.length > 0) {
                        // Print notifications
                        this.getTrucksEventsSinceLastEventId.map((event) => {
                            this.$snotify.confirm(
                                'Nuovo evento',
                                `${event.event_type} - ${event.truck_id}`,
                                {
                                    position: 'leftTop',
                                    timeout: 5000,
                                    buttons: [
                                        {
                                            text: 'Apri',
                                            action: () => {
                                                // this.truckFocus(event.truck)
                                            }
                                        },
                                        {
                                            text: 'Chiudi',
                                            action: (toast) => {
                                                this.$snotify.remove(toast.id)
                                            }
                                        }
                                    ]
                                }
                            )
                        })

                        // Update last event id
                        const lastEvent = val[val.length - 1]
                        this.$store.commit('setTrucksLastEventId', lastEvent.id)
                    }
                }
            }
        },
        computed: {
            // Maps a map context-menu action to the handler that runs it with the
            // reverse-geocoded location. Keeps handleMapContextAction a thin
            // dispatcher.
            mapContextHandlers() {
                return {
                    'plan-from': (location) => this.startPlannerFromMap(location, 'start'),
                    'plan-to': (location) => this.startPlannerFromMap(location, 'end'),
                    'vehicles-history': (location) => this.showVehiclesHistoryFromMap(location)
                }
            },
            blurredMap() {
                return (
                    this.activity_panel !== null ||
                    (this.utility_panel !== null &&
                        this.utility_panel != 'vehicles-passage' &&
                        this.utility_panel != 'trucks-with-alerts' &&
                        this.utility_panel != 'freight-exchange') ||
                    // Only the fluid table spans the whole row and hides the
                    // map behind it; a content-sized one leaves it visible.
                    (this.vehicleListModeView == 'table' &&
                        !this.$store.state.panels.main.collapsed &&
                        this.vehicleListFluidTable)
                )
            },
            listActiveTrucks() {
                // REFACTOR-026 (Sprint 13): Set per il lookup O(1) invece di indexOf
                // O(M) dentro il filter → da O(N×M) a O(N+M). Questa computed dipende
                // da storeTrucks → ricalcolata ad ogni polling (20s), non solo al
                // toggle filtro. Vedi INVESTIGATION-001 F8.
                const filteredOut = new Set(this.truck_filtered_out)
                return this.storeTrucks.filter((truck) => !filteredOut.has(truck.id))
            },
            // Sprint 19: reader granulari dal Config Bus.
            ...mapConfig(['activeVehicleClustering', 'vehicleListModeView', 'vehicleListFluidTable']),
            ...mapState({
                rootLoading: (state) => state.rootLoading,
                storeTrucks: (state) => state.geoFleet.trucks,
                trucksEvents: (state) => state.geoFleet.trucksEvents,
                bluetooth_devices: (state) => state.geoFleet.bluetooth_devices,
                truck_filtered_out: (state) => state.geoFleet.truckFilteredOut,
                resources: (state) => state.searchResource.resources,
                reachableAreasPolygons: (state) => state.searchResource.reachable_areas_polygons,
                mainMapStopAutocenter: (state) => state.geoFleet.mainMapStopAutocenter,
                mainMapExtraObjects: (state) => state.geoFleet.mainMapExtraObjects,
                // REFACTOR-007 (Sprint 7): istanza della main map (l-map vue component),
                // committata nel mounted. Usata per fitBounds al posto degli accessi
                // $refs.map.$refs.map / $refs.map.lmap (interni del figlio).
                mainMapInstance: (state) => state.geoFleet.mainMapInstance
            }),
            ...mapGetters({
                focus_truck: 'getFocusTruck',
                getTrucksEventsSinceLastEventId: 'getTrucksEventsSinceLastEventId',
                activity_truck: 'getActivityTruck',
                searchResourcesOpen: 'searchResourcesOpen',
                mainCollapsed: 'mainCollapsed',
                activityOpen: 'activityOpen',
                activityMeta: 'activityMeta',
                getTrucksWithAlerts: 'getTrucksWithAlerts',
                plafondOpen: 'plafondOpen',
                getFEUser: 'getFEUser',
                // REFACTOR-009: sostituisce il pattern locale `this.trucks_ref[id]`
                // con un getter O(1) derivato da state.geoFleet.trucks.
                trucksMap: 'trucksMap'
            }),
            menu_items() {
                let menu = []

                menu.push({
                    ref: 'tags',
                    title: this.__('tags.manage_tags'),
                    icon: 'fa-solid fa-tags',
                    attributes: {
                        title: this.__('tags.manage_tags')
                    }
                })

                if (this.$can('use', 'poi'))
                    menu.push({
                        ref: 'manage-poi',
                        title: this.__('manage_poi.title'),
                        icon: 'fak fa-poi',
                        attributes: {
                            title: this.__('manage_poi.title')
                        }
                    })

                if (this.$can('use', 'vehicles_cross'))
                    menu.push({
                        ref: 'vehicles-passage',
                        title: this.__('vehicles_cross.title'),
                        icon: 'fa-regular fa-radar',
                        attributes: {
                            title: this.__('vehicles_cross.title')
                        }
                    })

                if (this.$can('use', 'planner'))
                    menu.push({
                        ref: 'planner',
                        title: this.__('planner.plan_route'),
                        icon: 'fa-solid fa-route',
                        attributes: {
                            title: this.__('planner.plan_route')
                        }
                    })

                if (this.$can('use', 'notifications'))
                    menu.push({
                        ref: 'notifications',
                        title: this.__('notifications'),
                        icon: 'fa-solid fa-bell',
                        attributes: {
                            title: this.__('notifications')
                        }
                    })

                if (this.$can('use', 'share_link'))
                    menu.push({
                        ref: 'shared-links',
                        title: this.__('shared_links'),
                        icon: 'fa-solid fa-share-nodes',
                        attributes: {
                            title: this.__('shared_links')
                        }
                    })

                menu.push({
                    ref: 'reports',
                    title: this.__('reports'),
                    icon: 'fa-solid fa-chart-column',
                    attributes: {
                        title: this.__('reports')
                    }
                })

                if (this.bluetooth_devices.length > 0)
                    menu.push({
                        ref: 'bluethooth-devices',
                        title: this.__('bluetooth_devices'),
                        icon: 'fa-solid fa-bluetooth',
                        attributes: {
                            title: this.__('bluetooth_devices')
                        }
                    })
                if (this.getFEUser !== null) {
                    menu.push({
                        component: FreightExchangeMain,
                        props: {
                            onTogglePanel: (panel) => this.toggleUtilityPanel(panel),
                            collapsed: this.main_menu_collapsed,
                            utilityPanel: this.utility_panel
                        }
                    })
                }

                return menu
            }
        },
        methods: {
            truckFocus(truck, optional_actions, from_map_marker) {
                // Setto la proprità on_focus a true per il truck nello Store
                this.$store.commit('setFocusTruck', { truck: truck, on_focus: true })

                if (optional_actions)
                    optional_actions.forEach((action) => {
                        if (action.type == 'action') this.$store.dispatch(action.name, action.payload)
                        else if (action.type == 'mutation')
                            this.$store.commit(action.name, action.payload)
                    })

                // Se c'è un truck in attività e non è quello su cui ho cliccato, lo apro con il truck su cui ho cliccato
                if (this.activity_truck && this.activity_truck.id != truck.id)
                    this.utilsPanel(truck, this.activity_panel)

                // Se c'è un truck in attività e non c'era il panel aperto, lo apro con il truck su cui ho cliccato
                if (this.activity_panel && !this.activity_truck)
                    this.utilsPanel(truck, this.activity_panel)

                // REFACTOR-007 (Sprint 7): lo scroll della lista sul truck arriva ora dal
                // bus dello store (commit requestTruckFocus sotto) — MainPanel watcha
                // getFocusRequest. Rimossa la chiamata imperativa $refs.main_panel.truckFocus.

                // Barbatrucco: se è stato attivato un loader per diluire l'animazione lo disattivo dopo un timer di 0.5 secondi
                // BUG-018: stash su this._focusLoaderTimer per cleanup in beforeDestroy.
                // Clear timer precedente se truckFocus è chiamato in rapida sequenza.
                if (this.rootLoading) {
                    if (this._focusLoaderTimer) clearTimeout(this._focusLoaderTimer)
                    this._focusLoaderTimer = setTimeout(() => {
                        this.$store.commit('setLoading', false)
                        this._focusLoaderTimer = null
                    }, 500)
                }

                // REFACTOR-007 (Sprint 7): focus mappa via bus store invece di
                // $refs.map.truckFocus. GeofleetMap (isMainMap) watcha getFocusRequest e
                // applica setView rispettando noAutoCenterMap (= mainMapStopAutocenter):
                // il guard mainMapStopAutocenter vive ora nel watcher del figlio.
                this.$store.commit('requestTruckFocus', { truck, fromMapMarker: from_map_marker })

                if (
                    this.$route.name !== 'geofleet.vehicle_details' ||
                    this.$route.params.id !== truck.plate
                ) {
                    this.$router.push({ name: 'geofleet.vehicle_details', params: { id: truck.plate } })
                }
            },
            lostFocus() {
                if (!this.focus_truck) return false

                if (this.searchResourcesOpen) this.$store.commit('closePanel', 'searchResources')

                this.$store.commit('setFocusTruck', { truck: this.focus_truck, on_focus: false })

                this.expandPanel({ panel: 'main' })

                if (this.$route.name !== 'geofleet.index') {
                    this.$router.push({ name: 'geofleet.index' })
                }
            },
            // Focuses a truck (if not already focused) and navigates to one of its
            // sub-routes (vehicle_analyze, vehicle_plan, vehicle_details).
            focusAndNavigate(truck, routeName) {
                if (!truck) {
                    return
                }
                if (!this.focus_truck || this.focus_truck.id !== truck.id) {
                    this.$store.commit('setFocusTruck', { truck, on_focus: true })
                }
                this.$router.push({ name: routeName, params: { id: truck.plate } })
            },
            truckActivity(truck) {
                this.focusAndNavigate(truck, 'geofleet.vehicle_analyze')
            },
            truckPlanner(truck) {
                if (truck) {
                    this.focusAndNavigate(truck, 'geofleet.vehicle_plan')
                } else {
                    this.utilsPanel(null, 'planner')
                }
            },
            // Desktop-only map context-menu actions bubbled up from GeofleetMap.
            // Reverse-geocodes the clicked point, then delegates to the per-action
            // handler. ("create-poi" is handled in GeofleetMap via a route change,
            // so it never reaches here.)
            async handleMapContextAction({ action, lat, lng }) {
                const handler = this.mapContextHandlers[action]
                if (!handler) return

                const location = await this.buildContextLocation(lat, lng)
                handler(location)
            },
            // Reverse-geocode a clicked point into a { label, referenceCoordinate }
            // location, falling back to formatted coordinates when geocoding fails.
            async buildContextLocation(lat, lng) {
                const address = await this.$reverseGeocoding({ lat, lng, full: true }).catch(
                    () => null
                )
                return {
                    label: address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                    referenceCoordinate: { x: lng, y: lat }
                }
            },
            // Open the Vehicles Cross panel seeded with the picked location.
            showVehiclesHistoryFromMap(location) {
                this.$store.commit('setVehiclesCrossSeed', { address: location })
                this.toggleUtilityPanel('vehicles-passage')
            },
            startPlannerFromMap(location, startMode) {
                const picked = {
                    coordinate: {
                        referenceCoordinate: location.referenceCoordinate,
                        label: location.label
                    }
                }

                // 'start' → picked point is the departure; 'end' → the destination.
                const waypoints =
                    startMode === 'start'
                        ? [{ id: 1, ...picked }, { id: 2 }]
                        : [{ id: 1 }, { id: 2, ...picked }]

                this.$store.commit('updateWaypoints', waypoints)
                this.$store.commit('setPlannerSeed', { startMode })
                this.utilsPanel(null, 'planner')
            },
            utilsPanel(truck, type) {
                this.closePoiManagementRoute()

                if (this.utility_panel) this.utility_panel = null

                if (this.searchResourcesOpen) this.$store.commit('closePanel', 'searchResources')

                if (this.focus_truck && this.focus_truck.id != truck.id) this.truckFocus(truck)

                if (truck) this.$store.commit('setActivityTruck', { truck: truck, on_activity: true })

                this.activity_panel = type

                // REFACTOR-007 (Sprint 7): la classe 'with-activity-open' su MainPanel è ora
                // un binding reattivo (prop activityPanelOpen) invece di
                // $refs.main_panel.$el.classList. Il delay 1000ms (animazione) resta.
                // BUG-018: timer stashato per cleanup in beforeDestroy.
                if (this._utilsPanelOpenTimer) clearTimeout(this._utilsPanelOpenTimer)
                this._utilsPanelOpenTimer = setTimeout(() => {
                    this.activityPanelOpen = true
                    this._utilsPanelOpenTimer = null
                }, 1000)
            },
            lostUtilsPanel() {
                if (this.activity_truck) {
                    this.$store.commit('setActivityTruck', {
                        truck: this.activity_truck,
                        on_activity: false
                    })
                    this.$store.commit('closePanel', 'activity')
                }

                this.activity_panel = null

                // REFACTOR-007 (Sprint 7): vedi nota in utilsPanel. Binding reattivo
                // (prop activityPanelOpen) invece di $refs.main_panel.$el.classList.
                // BUG-018: timer stashato per cleanup in beforeDestroy.
                if (this._utilsPanelCloseTimer) clearTimeout(this._utilsPanelCloseTimer)
                this._utilsPanelCloseTimer = setTimeout(() => {
                    this.activityPanelOpen = false
                    this._utilsPanelCloseTimer = null
                }, 1000)
            },
            navigateToRoute(location) {
                const targetPath = this.$router.resolve(location).route.fullPath

                if (
                    targetPath === this.$route.fullPath ||
                    this._pendingRoutePath === targetPath
                ) {
                    return
                }

                this._pendingRoutePath = targetPath

                this.$router
                    .push(location)
                    .catch((error) => {
                        if (error?.name !== 'NavigationDuplicated') {
                            console.error('Router navigation failed:', error)
                        }
                    })
                    .finally(() => {
                        if (this._pendingRoutePath === targetPath) {
                            this._pendingRoutePath = null
                        }
                    })
            },
            closePoiManagementRoute() {
                if (this.$route.name !== 'geofleet.poi_management') return

                this.navigateToRoute({ name: 'geofleet.index' })
            },
            closePanelsBeforePoiManagement() {
                if (this._utilityPanelSwitchTimer) {
                    clearTimeout(this._utilityPanelSwitchTimer)
                    this._utilityPanelSwitchTimer = null
                }
                if (this._utilsPanelOpenTimer) {
                    clearTimeout(this._utilsPanelOpenTimer)
                    this._utilsPanelOpenTimer = null
                }

                this.utility_panel = null
                this.activity_panel = null
                this.activityPanelOpen = false

                if (this.activity_truck) {
                    this.$store.commit('setActivityTruck', {
                        truck: this.activity_truck,
                        on_activity: false
                    })
                }
                if (this.activityOpen) this.$store.commit('closePanel', 'activity')
                if (this.searchResourcesOpen) this.$store.commit('closePanel', 'searchResources')
                if (this.plafondOpen) this.$store.commit('closePlafondPanel', 'plafond')
            },
            toggleUtilityPanel(panel) {
                if (panel === 'notifications' && !this.$can('use', 'notifications')) {
                    this.utility_panel = null
                    return
                }
                // Some sidebar entries navigate to a routed page instead of
                // toggling the in-place utility_panel state.
                if (panel === 'manage-poi') {
                    this.closePanelsBeforePoiManagement()
                    this.navigateToRoute({ name: 'geofleet.poi_management' })
                    return
                }
                if (panel === 'settings') {
                    if (this.focus_truck) {
                        this.$store.commit('setFocusTruck', { truck: this.focus_truck, on_focus: false })
                    }
                    this.collapsePanel({ panel: 'main' })
                    this.navigateToRoute({
                        name: 'geofleet.settings',
                        query: { menu: 'general' }
                    })
                    return
                }

                if (panel) this.closePoiManagementRoute()

                if (this.utility_panel == panel) return

                if (this.activity_panel) this.activity_panel = null

                if (this.searchResourcesPanelOpen) this.$store.commit('toggleSearchResourcePanel')

                if (panel)
                    if (this.utility_panel) {
                        this.utility_panel = null

                        // BUG-018: stash su this._utilityPanelSwitchTimer per cleanup
                        // in beforeDestroy. Senza clear, set di utility_panel su istanza
                        // unmounted è no-op ma è lavoro sprecato.
                        if (this._utilityPanelSwitchTimer)
                            clearTimeout(this._utilityPanelSwitchTimer)
                        this._utilityPanelSwitchTimer = setTimeout(() => {
                            this.utility_panel = panel
                            this._utilityPanelSwitchTimer = null
                        }, 300)
                    } else {
                        this.utility_panel = panel
                    }
                else this.utility_panel = null
            },
            boundVisibleTruck() {
                let focusVisible = false

                let visibleTrucks = this.listActiveTrucks.map((truck) => {
                    if (this.focus_truck && truck.id == this.focus_truck.id) {
                        focusVisible = truck.visible
                    }

                    return [truck.lastpoint.lat, truck.lastpoint.lng]
                })

                if (focusVisible === false) this.lostFocus()

                // REFACTOR-007 (Sprint 7): $refs.map.lmap === mainMapInstance.mapObject (Leaflet raw)
                if (visibleTrucks.length && this.mainMapInstance?.mapObject)
                    this.mainMapInstance.mapObject.fitBounds(visibleTrucks, { padding: [50, 50] })
            },
            computeActivity(lastpoint) {
                if (lastpoint.speed || lastpoint.tacho_speed) return 'moving'

                if (lastpoint.engine_rpm) return 'engine_on'

                if (lastpoint.ignition) return 'ignition'

                return 'off'
            },
            loadPoisList(pois) {
                // Nota: NON applichiamo deepFreeze qui — l'array `pois` è la stessa reference
                // owned da GeofleetMap.vue (emessa via $emit('poisLoaded', this.pois)) e il watcher
                // del figlio su poisForm muta `poi.visible = p.selected`. Freezarlo rompe il toggle.
                this.pois = pois
            },
            loadTrucks(sb) {
                this.last_update = moment.utc().toISOString()

                this.$fetch(
                    this.$env.GOLIA_API +
                    '/trucks?include[]=lastpoint&include[]=details&include[]=category&include[]=tags&filter[VISIBILE][eq]=1'
                )
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        return res.data
                    })
                    .then((data) => {
                        sb.done('trucks', data)
                    })
            },
            loadDrivers(sb) {
                this.$fetch(this.$env.GOLIA_API + '/geofleet/drivers')
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        sb.done('drivers', res.data)
                    })
            },
            loadProfiles(sb) {
                this.$fetch(this.$env.GOLIA_API + '/geofleet/profiles')
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        sb.done('profiles', res.data)
                    })
            },
            async loadGoliaExchange() {
                const fe = JSON.parse(sessionStorage.getItem('freight_exchange'))
                const tokenExpired = fe && fe !== 401 ? new Date(fe.expires_at) < new Date() : true

                if (
                    this.$can('use', 'freight_exchange') &&
                    this.$can('use', 'golia_exchange') &&
                    tokenExpired
                ) {
                    const userId = this.$store.state.loggedUser?.logged_user?.payload?.id
                    const loginToken = this.$env.GOLIA_EXCHANGE_LOGIN_TOKEN

                    if (!loginToken) {
                        console.error(
                            'Golia Exchange is not configured: set VUE_APP_GOLIA_EXCHANGE_LOGIN_TOKEN'
                        )
                        return
                    }

                    if (userId) {
                        await fetch(`${this.$env.GOLIA_EXCHANGE_API}/login/${userId}`, {
                            method: 'GET',
                            headers: {
                                authorization: loginToken
                            }
                        })
                            .then((res) => {
                                if (!res.ok || res.status == 401) {
                                    this.setFreightExchangeUser(401)
                                    sessionStorage.setItem('freight_exchange', 401)
                                    return Promise.reject()
                                } else {
                                    return res.json()
                                }
                            })
                            .then((res) => {
                                this.setFreightExchangeUser(res.access_token)
                                sessionStorage.setItem(
                                    'freight_exchange',
                                    JSON.stringify(res.access_token)
                                )
                            })
                    }
                } else {
                    this.setFreightExchangeUser(fe)
                }
            },
            loadProveniences(sb) {
                this.$fetch(this.$env.GOLIA_API + '/proveniences')
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        sb.done('proveniences', res.data)
                    })
            },
            loadCompanies(sb) {
                this.$fetch(this.$env.GOLIA_API + '/companies')
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        let companies = {}

                        companies[res.data.company.id] = res.data.company.business_name

                        for (var cmpny of res.data.master_slave) {
                            companies[cmpny.id] = cmpny.business_name
                        }

                        // REFACTOR-018: niente deepFreeze qui — la mutation setCompanies
                        // freeza la copia store. Single freeze point per la collezione.
                        this.companies = companies

                        sb.done('companies', this.companies)
                    })
            },
            // BUG-006: recovery quando il polling lastpoints riferisce un truck non noto
            // a trucks_ref (es. veicolo registrato lato Golia a session aperta).
            // Fire-and-forget: il punto attuale è ignorato, ma il truck viene aggiunto
            // al successivo polling (lag max 20s) preservando lo stato della sessione utente.
            fetchAndAddTruck(truckId) {
                if (!this._pendingTruckFetches) this._pendingTruckFetches = new Set()
                if (this._pendingTruckFetches.has(truckId)) return
                this._pendingTruckFetches.add(truckId)

                this.$fetch(
                    this.$env.GOLIA_API +
                    '/trucks?include[]=lastpoint&include[]=details&include[]=category&include[]=tags&filter[VISIBILE][eq]=1&filter[id][eq]=' +
                    truckId
                )
                    .then((res) => res.json())
                    .then((res) => {
                        // BUG-017: destroy-guard. Se il componente è stato unmount-ato durante
                        // la fetch in-flight (route change, switch desktop↔mobile su resize),
                        // saltare le mutation: il bootstrap della prossima istanza ricostruirà
                        // trucks_ref e store da zero, e committare adesso lascerebbe entry zombie.
                        if (this._isDestroyed) return

                        const v = (res.data || []).find((x) => x && x.lastpoint && x.visible)
                        if (!v) {
                            console.warn('BUG-006 recovery: truck not returned or invalid', {
                                truckId
                            })
                            return
                        }

                        // REFACTOR-017: hydration condivisa con created.finally.
                        // Counter atomico evita la race REFACTOR-012 (Object.keys.length leggeva
                        // lo stesso valore se due fetchAndAddTruck concorrenti si overlap-pavano).
                        this.hydrateTruck(v, this._truckIndexCounter++)

                        // REFACTOR-009: niente più scrittura locale `trucks_ref[v.id] = ...`.
                        // BUG-016 reference aliasing era una conseguenza del doppio storage:
                        // adesso eliminato, l'unico canale di scrittura è la mutation
                        // appendTruck. Il mutation clona shallow il payload, quindi state
                        // e trucksMap-derived sono coerenti senza ulteriori split.
                        // BUG-014: appendTruck PRIMA di findTrailerHooked, così v è già in
                        // state.trucks quando findTrailerHooked usa trucksMap per matching.
                        this.$store.commit('appendTruck', v)
                        // REFACTOR-009: rimosso `setTrucksRef` commit — i consumer cross-file
                        // (VehiclesCrossResults) usano ora il getter trucksMap derivato da
                        // state.trucks, popolato direttamente da appendTruck.

                        this.findTrailerHooked(v)
                        // BUG-008 (assorbito in BUG-014): allineato con created.finally + updateData
                        // che chiamano sempre findTrailerHooked seguito da cleanInvalidTrailerConnections.
                        this.cleanInvalidTrailerConnections()
                    })
                    .catch((err) => {
                        console.warn('BUG-006 recovery: truck fetch failed', { truckId, err })
                    })
                    .finally(() => {
                        // BUG-017: anche il cleanup del Set deve essere safe post-destroy.
                        if (this._isDestroyed) return
                        if (this._pendingTruckFetches) this._pendingTruckFetches.delete(truckId)
                    })
            },
            updateData() {
                // BUG-019: guard contro bootstrap-race. Su bootstrap lento (>20s, mobile
                // su segnale debole o backend congestionato), l'_lastpointsInterval può
                // fire prima di sb.finally(): trucks_ref={} → tutti i truck cadono in
                // fetchAndAddTruck → appendTruck cresce state.trucks → finally() poi
                // setTrucks(...) wipe i recovered, ma trucks_ref li mantiene → zombie
                // (polling successivi vedono trucks_ref[id], skip recovery, ma
                // mergeTrucksDelta no-op perchè id non in state.trucks).
                if (!this._bootstrapComplete || this._lastpointsRequestPending) return

                this._lastpointsRequestPending = true

                let query = new URLSearchParams(
                    this.last_update ? { update: this.last_update } : ''
                ).toString()

                this.last_update = moment.utc().toISOString()

                return this.$fetch(
                    this.$env.GOLIA_API + '/geofleet/lastpoints' + (query ? '?' + query : '')
                )
                    .then((res) => res.json())
                    .then((res) => {
                        // Get the currently focused and active trucks before updates
                        const currentlyFocusedTruck = this.focus_truck
                        const currentlyActivityTruck = this.activity_truck

                        // REFACTOR-011: accumula tutti gli update in un array e committa una volta sola
                        // a fine ciclo, invece di N commit('updateTruck') consecutivi.
                        const delta = []

                        res.data.forEach((p) => {
                            // REFACTOR-009: lookup id→truck via getter trucksMap (O(1)).
                            // Sostituisce `this.trucks_ref[p.truck]` locale.
                            const existingTruck = this.trucksMap.get(p.truck)
                            if (!existingTruck) {
                                // BUG-006: era `location.reload()` → svuotava la sessione utente
                                // (filtri, pannelli, focus, mappa) per gestire un truck nuovo.
                                // Ora fetch mirato fire-and-forget: il truck sarà disponibile
                                // dal prossimo polling (lag massimo 20s, sessione preservata).
                                this.fetchAndAddTruck(p.truck)
                                return
                            }

                            // Create a new truck object with updated properties while preserving states
                            const updatedTruck = {
                                ...existingTruck,
                                activity_type: this.computeActivity(p),
                                lastpoint: this.bindLastPointRelations(p),
                                on_focus:
                                    currentlyFocusedTruck?.id === p.truck
                                        ? currentlyFocusedTruck.on_focus
                                        : existingTruck.on_focus,
                                on_activity:
                                    currentlyActivityTruck?.id === p.truck
                                        ? currentlyActivityTruck.on_activity
                                        : existingTruck.on_activity
                            }

                            delta.push({ truck: updatedTruck })
                            // REFACTOR-009: niente più scrittura locale `trucks_ref[p.truck] = ...`.
                            // BUG-016 split via destructuring non serve più: lo store è single
                            // source of truth, il commit mergeTrucksDelta a fine ciclo aggiorna
                            // state.trucks da cui trucksMap deriva.

                            // Update trailer relationships — passa delta per accumulare anche i trailer
                            this.findTrailerHooked(updatedTruck, delta)
                        })

                        // REFACTOR-016: cleanup defer commit. Sarà committato in unico colpo
                        // a fine ciclo insieme a mergeTrucksDelta.
                        this.cleanInvalidTrailerConnections(true)

                        // Handle trailers visibility
                        this.truck_hooked_list.forEach((item) => {
                            // REFACTOR-009: iterazione per plate via storeTrucks (state.geoFleet.trucks).
                            // Riflette le commit PRECEDENTI; gli update del ciclo corrente sono
                            // ancora in `delta` (saranno committed via mergeTrucksDelta a fine forEach).
                            // OK perchè qui usiamo il trailer solo come SOURCE per spread (`{ ...trailer, ... }`)
                            // — il write effettivo va via delta.push, non via mutation locale.
                            const trailer = this.storeTrucks.find(
                                (t) => t.plate === item.trailer
                            )

                            if (trailer) {
                                const updatedTrailer = {
                                    ...trailer,
                                    visible_on_map: false,
                                    on_focus:
                                        currentlyFocusedTruck?.id === trailer.id
                                            ? currentlyFocusedTruck.on_focus
                                            : trailer.on_focus,
                                    on_activity:
                                        currentlyActivityTruck?.id === trailer.id
                                            ? currentlyActivityTruck.on_activity
                                            : trailer.on_activity
                                }
                                delta.push({ truck: updatedTrailer })
                            }
                        })

                        // REFACTOR-016: setTrailerHooked committato qui una sola volta per
                        // tutto il ciclo (findTrailerHooked × N + cleanInvalidTrailerConnections
                        // hanno skippato il commit perchè in batched context). Subscribers
                        // di state.geoFleet.truck_hooked_list (LTruckMarker, ecc.) ricevono
                        // 1 trigger reattivo invece di N+1 per polling cycle.
                        this.$store.commit('setTrailerHooked', this.truck_hooked_list)

                        // Commit unico: un solo trigger reattivo per tutti gli update del ciclo.
                        if (delta.length > 0) {
                            this.$store.commit('mergeTrucksDelta', delta)
                        }
                    })
                    .catch((error) => {
                        console.error('Error updating truck data:', error)
                    })
                    .finally(() => {
                        this._lastpointsRequestPending = false
                    })
            },
            updateTruckTags(tags, truck) {
                this.$store.commit('updateTruckProperty', {
                    truck: truck,
                    property: 'tags',
                    value: tags
                })
            },
            // REFACTOR-017: hydrazione "raw vehicle JSON → truck object UI-ready".
            // Chiamato dal bootstrap (created.finally) e dalla recovery (fetchAndAddTruck).
            // Assorbe BUG-007 (typo v.length → v.details.length) e REFACTOR-012 (counter
            // atomico this._truckIndexCounter, eliminato Object.keys(trucks_ref).length race).
            // NON include findTrailerHooked né trucks_ref assignment — caller decide ordering
            // e split (vedi BUG-016).
            hydrateTruck(v, index) {
                v.visible = true
                v.on_focus = false
                v.pinned = false
                v.on_activity = false
                v.visible_on_map = true
                v.visible_on_list = true
                v.index = index
                v.details = v.details
                    ? v.details.length === 1
                        ? v.details[0]
                        : v.details.find((d) => d.id_main_descr == 0)
                    : null
                v.activity_type = this.computeActivity(v.lastpoint)
                v.profile = this.profiles[v.details ? v.details.profile_id : null]
                v.lastpoint = this.bindLastPointRelations(v.lastpoint)
                return v
            },
            bindLastPointRelations(lastpoint) {
                // Clone shallow di tutte le shared references verso le collezioni deep-frozen
                // del bootstrap (drivers, proveniences). Consumer downstream (PlannerPanelContents,
                // DriversSelection, ecc.) mutano campi su driver/codriver — la frozen reference
                // farebbe TypeError. Cloning sempre, anche quando lastpoint.state non c'è.
                // BUG-010: guard sul lookup — se l'id non è nella collezione (es. autista
                // disattivato tra bootstrap e polling), `this.drivers[id]` è undefined e
                // `{ ...undefined }` collassa a `{}` (truthy ma vuoto), che ribalta i v-if
                // dei consumer e renderizza badge driver con tutti i campi undefined.

                // BUG-015: prima del clone, memorizzare lo state del driver/codriver del
                // ciclo precedente. Se il payload corrente OMETTE `lastpoint.state` (delta
                // payload optimization quando lo state non cambia), il clone fresco da
                // `this.drivers[id]` non avrebbe `.state` → badge driver_status sparirebbe
                // su un ciclo, riapparirebbe sul successivo (flicker). Match per id: se
                // l'id driver è cambiato tra cicli, NON propagare lo state vecchio.
                // REFACTOR-009: previous lastpoint via trucksMap (O(1) lookup).
                const previousTruck = lastpoint.truck ? this.trucksMap.get(lastpoint.truck) : null
                const previousLastpoint = previousTruck ? previousTruck.lastpoint : null
                const previousDriverState =
                    previousLastpoint &&
                        previousLastpoint.driver &&
                        previousLastpoint.driver.id === lastpoint.driver
                        ? previousLastpoint.driver.state
                        : null
                const previousCodriverState =
                    previousLastpoint &&
                        previousLastpoint.codriver &&
                        previousLastpoint.codriver.id === lastpoint.codriver
                        ? previousLastpoint.codriver.state
                        : null

                lastpoint.provenience =
                    lastpoint.provenience && this.proveniences[lastpoint.provenience]
                        ? { ...this.proveniences[lastpoint.provenience] }
                        : null
                lastpoint.driver =
                    lastpoint.driver && this.drivers[lastpoint.driver]
                        ? { ...this.drivers[lastpoint.driver] }
                        : null
                lastpoint.last_driver =
                    lastpoint.last_driver && this.drivers[lastpoint.last_driver]
                        ? { ...this.drivers[lastpoint.last_driver] }
                        : null
                lastpoint.codriver =
                    lastpoint.codriver && this.drivers[lastpoint.codriver]
                        ? { ...this.drivers[lastpoint.codriver] }
                        : null
                lastpoint.company_name = this.companies[lastpoint.company] || ''

                //inserimento dello stato autista a driver, codriver e rfid

                /**
                 * TODO: Temporaneamente ignoriamo l'id 5 perchè corrisponde a "nessuno_stato"
                 * TODO: Da aggiornare inserendo la logica dei permessi e controllare per il permesso GoliaRoad
                 */
                const id_to_ignore = 5

                if (lastpoint.state) {
                    if (lastpoint.state.driver && lastpoint.state.driver.id !== id_to_ignore) {
                        if (lastpoint.state.driver.company_id == 1) {
                            lastpoint.state.driver.description = lastpoint.state.driver.description
                                .replaceAll(' ', '_')
                                .toLowerCase()
                        }
                        delete lastpoint.state.driver.id_golia
                        if (lastpoint.driver) {
                            // lastpoint.driver è già una copia (cloning sopra), mutazione safe.
                            lastpoint.driver.state = { ...lastpoint.state.driver }
                        }
                    }
                    if (lastpoint.state.codriver && lastpoint.state.codriver.id !== id_to_ignore) {
                        if (lastpoint.state.codriver.company_id == 1) {
                            lastpoint.state.codriver.description = lastpoint.state.codriver.description
                                .replaceAll(' ', '_')
                                .toLowerCase()
                        }
                        delete lastpoint.state.codriver.id_golia
                        if (lastpoint.codriver) {
                            lastpoint.codriver.state = { ...lastpoint.state.codriver }
                        }
                    }
                    if (lastpoint.state.rfid && lastpoint.state.rfid.id !== id_to_ignore) {
                        if (lastpoint.state.rfid.company_id == 1) {
                            lastpoint.state.rfid.description = lastpoint.state.rfid.description
                                .replaceAll(' ', '_')
                                .toLowerCase()
                        }
                        delete lastpoint.state.rfid.id_golia
                        if (lastpoint.rfid) {
                            lastpoint.rfid.state = { ...lastpoint.state.rfid }
                        }
                    }
                } else {
                    // BUG-015: il payload omette `state` (delta payload, stato invariato).
                    // Carry forward del state da driver/codriver del ciclo precedente,
                    // se l'id è rimasto lo stesso. Niente carry forward per `rfid` (il
                    // pattern original sopra non lo conserva tra cicli neanche oggi).
                    if (lastpoint.driver && previousDriverState) {
                        lastpoint.driver.state = previousDriverState
                    }
                    if (lastpoint.codriver && previousCodriverState) {
                        lastpoint.codriver.state = previousCodriverState
                    }
                }
                try {
                    lastpoint.telemetry =
                        (typeof lastpoint.telemetry === 'string'
                            ? JSON.parse(lastpoint.telemetry)
                            : lastpoint.telemetry) || {}
                } catch (err) {
                    lastpoint.telemetry = {}
                }

                return lastpoint
            },
            // delta opzionale (REFACTOR-011): se passato, i trailer da rendere visibili
            // sono accumulati nel delta invece di committare uno-a-uno.
            // truckSource opzionale (REFACTOR-009): default this.storeTrucks; il bootstrap
            // (sb.finally) lo override con l'array locale `trucks` in-progress, perchè
            // a quel punto state.trucks non è ancora committed.
            findTrailerHooked(truck, delta = null, truckSource = null) {
                const trucks = truckSource || this.storeTrucks
                let trailersToBeVisible = []

                // First, remove all existing entries for this truck to avoid duplicates
                const existingEntries = this.truck_hooked_list.filter(
                    (item) => item.truck === truck.plate
                )
                existingEntries.forEach((item) => {
                    const trailer = trucks.find((t) => t.plate === item.trailer)
                    if (trailer) {
                        trailersToBeVisible.push(item.trailer)
                    }
                })

                // Remove all entries for this truck
                this.truck_hooked_list = this.truck_hooked_list.filter(
                    (item) => item.truck !== truck.plate
                )

                // Now check if this truck has a valid trailer connection
                if (truck.lastpoint?.trailer) {
                    if (
                        truck.lastpoint?.trailer?.timestamp?.$date?.$numberLong &&
                        moment().diff(
                            moment.unix(truck.lastpoint?.trailer?.timestamp?.$date?.$numberLong / 1000),
                            'minutes'
                        ) < 7 &&
                        (!truck.lastpoint?.engine_rpm || truck.lastpoint?.engine_rpm > 0)
                    ) {
                        // Add the new trailer connection
                        this.truck_hooked_list.push({
                            truck: truck.plate,
                            truckObj: truck,
                            engine_rpm: truck.lastpoint?.engine_rpm,
                            trailer: truck.lastpoint.trailer.plate,
                            trailer_timestamp: truck.lastpoint.trailer.timestamp?.$date?.$numberLong,
                            formatted_timestamp:
                                moment
                                    .unix(truck.lastpoint.trailer.timestamp?.$date?.$numberLong / 1000)
                                    .format('DD/MM/YYYY HH:mm:ss') +
                                '(minutes ago: ' +
                                moment().diff(
                                    moment.unix(
                                        truck.lastpoint.trailer.timestamp?.$date?.$numberLong / 1000
                                    ),
                                    'minutes'
                                ) +
                                ')'
                        })

                        // Remove this trailer from the list of trailers to be made visible
                        trailersToBeVisible = trailersToBeVisible.filter(
                            (trailer) => trailer !== truck.lastpoint.trailer.plate
                        )
                    }
                }

                // REFACTOR-016: quando chiamato dal batched loop di updateData (delta != null),
                // SKIP del commit setTrailerHooked — il caller committa una sola volta a fine
                // ciclo. Senza questo skip, su una flotta di 200 truck con trailer, la chain
                // reattiva (LTruckMarker, mapState truck_hooked_list, ...) re-evaluava 200
                // volte invece di 1, vanificando il batch di REFACTOR-011.
                // Non-batched callers (created.finally, fetchAndAddTruck) committano subito.
                if (!delta) {
                    this.$store.commit('setTrailerHooked', this.truck_hooked_list)
                }

                trailersToBeVisible.forEach((trailerPlate) => {
                    const trailer = trucks.find((t) => t.plate === trailerPlate)

                    if (trailer) {
                        const updatedTrailer = { ...trailer, visible_on_map: true }
                        if (delta) {
                            delta.push({ truck: updatedTrailer })
                        } else {
                            this.$store.commit('updateTruck', { truck: updatedTrailer })
                        }
                    }
                })
            },
            // truckSource opzionale (REFACTOR-009): default this.storeTrucks; il bootstrap
            // lo override con l'array `trucks` in-progress (state.trucks non ancora committed).
            cleanInvalidTrailerConnections(deferCommit = false, truckSource = null) {
                const trucks = truckSource || this.storeTrucks
                // Remove all invalid trailer connections
                this.truck_hooked_list = this.truck_hooked_list.filter((item) => {
                    // Find the truck by plate
                    const truck = trucks.find((t) => t.plate === item.truck)

                    // If truck doesn't exist anymore, remove the entry
                    if (!truck) {
                        return false
                    }

                    // Check if trailer timestamp is still valid (< 7 minutes)
                    if (
                        item.trailer_timestamp &&
                        moment().diff(moment.unix(item.trailer_timestamp / 1000), 'minutes') >= 7
                    ) {
                        return false
                    }

                    // Check if truck still has a trailer connection
                    if (!truck.lastpoint?.trailer) {
                        return false
                    }

                    // Check if the trailer plate matches
                    if (truck.lastpoint.trailer.plate !== item.trailer) {
                        return false
                    }

                    // Check if engine_rpm is valid (doesn't exist or > 0)
                    if (
                        truck.lastpoint?.engine_rpm !== undefined &&
                        truck.lastpoint?.engine_rpm !== null &&
                        truck.lastpoint?.engine_rpm === 0
                    ) {
                        return false
                    }

                    // Entry is valid
                    return true
                })

                // REFACTOR-016: quando chiamato dal batched loop (deferCommit=true),
                // skip del commit — sarà il caller a committare a fine ciclo.
                if (!deferCommit) {
                    this.$store.commit('setTrailerHooked', this.truck_hooked_list)
                }
            },
            handleClick(reference) {
                if (typeof reference === 'function') {
                    reference()
                }
            },
            ...mapActions({
                fitBoundsOfMap: 'fitBoundsOfMap'
            }),
            ...mapMutations({
                focusResource: 'focusResource',
                collapsePanel: 'collapsePanel',
                expandPanel: 'expandPanel',
                setFreightExchangeUser: 'setFreightExchangeUser'
            })
        },
        created() {
            let _this = this
            this.loading = true

            let sb = {
                counter: 0,
                processes: 5,
                data: {},

                done: function (key, data) {
                    this.counter += 1

                    this.data[key] = data

                    if (this.counter >= this.processes) this.finally()
                },
                finally: function () {
                    _this.loading = false

                    // REFACTOR-018: niente deepFreeze su drivers — la mutation setDrivers
                    // freeza la copia store (single freeze point per collezione committata).
                    _this.drivers = this.data['drivers'].reduce((c, d) => {
                        c[d.id] = d
                        return c
                    }, {})

                    // proveniences/profiles non hanno mutation dedicate (sono view-local),
                    // quindi qui è l'unico punto di freeze. Mantenuto per REFACTOR-008.
                    _this.proveniences = deepFreeze(
                        this.data['proveniences'].reduce((c, d) => {
                            c[d.id] = d
                            return c
                        }, {})
                    )

                    _this.profiles = deepFreeze(
                        this.data['profiles'].reduce((c, d) => {
                            c[d.id] = d
                            return c
                        }, {})
                    )

                    // REFACTOR-017 + REFACTOR-012: counter atomico inizializzato qui, condiviso
                    // con fetchAndAddTruck (recovery) per produrre v.index monotoni globali e
                    // senza race tra bootstrap e recovery concorrente.
                    _this._truckIndexCounter = 0

                    // BUG-005: era `_this.trucks` (proprietà non dichiarata in data() → non reattiva).
                    // Usata solo qui in finally(), let locale.
                    //
                    // REFACTOR-009: hydration prima, findTrailerHooked dopo (due passi
                    // separati). Necessario per evitare TDZ: dentro `.map((v) => ...)` non
                    // si può ancora referenziare `trucks` perchè è in fase di assegnazione.
                    // Splittiamo: prima costruiamo `trucks` con la hydration, poi un secondo
                    // forEach invoca findTrailerHooked passando `trucks` come truckSource
                    // (state.trucks non ancora committed).
                    const trucks = this.data['trucks']
                        .filter((v) => v.lastpoint && v.visible)
                        .map((v) => {
                            _this.hydrateTruck(v, _this._truckIndexCounter++)
                            return v
                        })

                    trucks.forEach((v) => {
                        _this.findTrailerHooked(v, null, trucks)
                    })

                    // Clean invalid trailer connections after initial load (truckSource = trucks)
                    _this.cleanInvalidTrailerConnections(false, trucks)

                    _this.truck_hooked_list.forEach((item) => {
                        const trailer = trucks.find((t) => t.plate === item.trailer)

                        if (!trailer) {
                            console.warn('Trailer not found for hooked item', item)
                            return
                        }

                        // REFACTOR-009: niente più `trailerRef` (era _this.trucks_ref[trailer.id]).
                        // `trailer` qui è la STESSA reference che entrerà in setTrucks(trucks):
                        // mutarlo modifica il truck che sarà committed. Coerente con bootstrap
                        // pre-REFACTOR-009 dove trailer e trailerRef erano lo stesso oggetto.
                        trailer.visible_on_map = false
                    })

                    _this.$store.commit('setTrucks', trucks)
                    // REFACTOR-009: rimosso `setTrucksRef` commit — il getter trucksMap
                    // è derivato da state.trucks, niente più storage parallelo.
                    _this.$store.commit('setDrivers', _this.drivers)
                    _this.$store.commit('setCompanies', _this.companies)

                    // BUG-019: il flag deve essere settato DOPO le commit del bootstrap.
                    // Prima di questo punto, updateData non deve girare — altrimenti il
                    // polling (interval 20s) può fire prima del finally() su bootstrap
                    // lenti (>20s), ogni truck cade nel ramo fetchAndAddTruck, e poi
                    // setTrucks(trucks) wipe i recovered → zombie in trucks_ref.
                    _this._bootstrapComplete = true
                    _this.updateData()

                    // Deep-link: if the app is opened directly on a vehicle route,
                    // focus the truck in the store without pushing a new route —
                    // truckFocus() always navigates to vehicle_details which would
                    // overwrite sub-routes like driver_plafond or vehicle_plan.
                    if (_this.$route.params.id) {
                        const truck = trucks.find(t => t.plate === _this.$route.params.id)
                        if (truck) {
                            _this.$store.commit('setFocusTruck', { truck, on_focus: true })
                            _this.$store.commit('requestTruckFocus', { truck, fromMapMarker: false })
                        } else {
                            _this.$router.push({ name: 'geofleet.index' })
                        }
                    }
                }
            }

            this.loadDrivers(sb)
            this.loadProfiles(sb)
            this.loadProveniences(sb)
            this.loadGoliaExchange(sb)
            this.loadCompanies(sb)
            this.loadTrucks(sb)

            //Load common configs from Vuex
            // Sprint 19 (REFACTOR-030): direttamente `config/load` (shim legacy ritirato).
            this.$store.dispatch('config/load', {
                companyId: this.$store.state.loggedUser.params.Azienda || null
            })

            //Load company tags
            this.$store.dispatch('loadCompanyTags', this)
            this.$store.dispatch('loadPoiCategories', { vueInstance: this })

            //Load drivers data
            this.$store.dispatch('loadDriversData', { vueInstance: this })

            // Load bluetooth devices
            this.$store.dispatch('loadBluetoothDevices', { vueInstance: this })

            // this.$store.dispatch('loadTrucksEvents', {
            //     vueInstance: this,
            //     update: moment.utc().toISOString()
            // })

            // REFACTOR-005: handle salvati in proprietà non-reactive (prefisso _ ignorato da Vue)
            // così beforeDestroy può cancellarli su unmount (route change, hot-reload, switch desktop↔mobile).
            this._lastpointsInterval = setInterval(() => {
                if (this.$isBrowserWindowActive()) {
                    this.updateData()

                    // this.$store.dispatch('loadTrucksEvents', {
                    //     vueInstance: this,
                    //     update: moment.utc().toISOString()
                    // })
                }
            }, 20 * 1000)

            this._auxDataInterval = setInterval(() => {
                if (this.$isBrowserWindowActive()) {
                    this.$store.dispatch('loadDriversData', {
                        vueInstance: this,
                        update: moment.utc().toISOString()
                    })

                    this.$store.dispatch('loadBluetoothDevices', { vueInstance: this })
                }
            }, 300 * 1000)

            this._loadingTimeoutTimer = setTimeout(() => {
                if (this.loading) this.loadingTimeout = true
            }, 20000)
        },
        mounted() {
            if (this.$refs.map.$refs.map)
                this.$store.commit('setMainMapInstance', this.$refs.map.$refs.map)

            // Config Bus (Sprint 17, REFACTOR-030): i writer di config scrivono ora via
            // `config/commitChange`, che persiste con un flush DEBOUNCED (~600ms coalescing)
            // invece del POST immediato del vecchio `saveConfig`. Se l'utente chiude/ricarica
            // la tab entro la finestra di debounce, il burst pendente andrebbe perso → flush
            // best-effort su `beforeunload`. La navigazione SPA (unmount) è coperta da beforeDestroy.
            this._configFlushOnUnload = () => this.$store.dispatch('config/flush')
            window.addEventListener('beforeunload', this._configFlushOnUnload)
        },
        beforeDestroy() {
            // BUG-017: flag consumato dai callback fire-and-forget di fetchAndAddTruck
            // per early-return ed evitare mutation post-unmount sullo store globale.
            this._isDestroyed = true

            // Config Bus (Sprint 17): flush del config pendente (debounce ~600ms) prima di
            // lasciare la view via navigazione SPA, poi rimozione del listener di unload.
            this.$store.dispatch('config/flush')
            if (this._configFlushOnUnload) {
                window.removeEventListener('beforeunload', this._configFlushOnUnload)
                this._configFlushOnUnload = null
            }

            // REFACTOR-007 (Sprint 7): reset di mainMapInstance su unmount per evitare che
            // i consumer dello store (fitBoundsOfMap, FreightExchange) operino su un'istanza
            // Leaflet distrutta dopo che si lascia la view. Il prossimo mounted la ricommit.
            this.$store.commit('setMainMapInstance', null)

            // REFACTOR-005: cleanup dei timer creati in created() per evitare polling residui
            // dopo l'unmount del componente.
            if (this._lastpointsInterval) {
                clearInterval(this._lastpointsInterval)
                this._lastpointsInterval = null
            }
            if (this._auxDataInterval) {
                clearInterval(this._auxDataInterval)
                this._auxDataInterval = null
            }
            if (this._loadingTimeoutTimer) {
                clearTimeout(this._loadingTimeoutTimer)
                this._loadingTimeoutTimer = null
            }

            // BUG-018: cleanup dei 4 setTimeout one-shot delle animazioni (truckFocus,
            // utilsPanel, lostUtilsPanel, toggleUtilityPanel). Callback firing post-unmount
            // causano: setLoading(false) su next view (flicker), classList su elemento
            // undefined (TypeError), set utility_panel su istanza morta (no-op).
            if (this._focusLoaderTimer) {
                clearTimeout(this._focusLoaderTimer)
                this._focusLoaderTimer = null
            }
            if (this._utilsPanelOpenTimer) {
                clearTimeout(this._utilsPanelOpenTimer)
                this._utilsPanelOpenTimer = null
            }
            if (this._utilsPanelCloseTimer) {
                clearTimeout(this._utilsPanelCloseTimer)
                this._utilsPanelCloseTimer = null
            }
            if (this._utilityPanelSwitchTimer) {
                clearTimeout(this._utilityPanelSwitchTimer)
                this._utilityPanelSwitchTimer = null
            }
        }
    }
</script>

<style scoped lang="scss">
    @use './GeoFleet.scss';
</style>
